// /apps/patient/src/pages/Dashboard.tsx

import { useNavigate } from 'react-router-dom';
import { usePatient } from '../contexts/PatientContext';
import { useFeatures } from '../contexts/FeatureContext';
import { useState, useEffect } from 'react';
import { patientApiService } from '../services/api';
import {
  Calendar, FileText, Heart, Dumbbell,
  Clock, MessageSquare, Activity, TrendingUp,
  ChevronRight, AlertCircle, CheckCircle, Users
} from 'lucide-react';
import { format, isToday, isTomorrow, formatDistanceToNow, startOfWeek } from 'date-fns';
import { TodaysWorkoutCard } from '../components/dashboard/TodaysWorkoutCard';
import { MotivationCard } from '../components/dashboard/MotivationCard';
import { ExerciseNowCard } from '../components/dashboard/ExerciseNowCard';
import { ExerciseNowModal } from '../components/programs/ExerciseNowModal';

// Helper functions for day conversion
const jsToISODay = (jsDay: number): number => jsDay === 0 ? 7 : jsDay;
const isoDayToJS = (isoDay: number): number => isoDay === 7 ? 0 : isoDay;

interface TodayActivity {
  id: string;
  type: 'appointment' | 'exercise' | 'class';
  time?: string;
  title: string;
  subtitle?: string;
  status?: string;
  icon: any;
  color: string;
  action?: () => void;
  _workoutData?: {
    id: string;
    programName: string;
    workoutName?: string;
    exerciseCount: number;
    estimatedMinutes?: number;
    assignmentType: 'workout_plan' | 'rehab_program' | 'exercise_prescription';
    prescriptionId?: string;
  };
  _classData?: {
    enrollment_id: string;
    class_session_id?: string;
    class_definition_id?: string;
  };
}

interface RecentActivity {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: number;
  icon: any;
}

export function Dashboard() {
  const { patient } = usePatient();
  const { features, hasFeature, isStandalone, isExercisePlatform } = useFeatures();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [todayActivities, setTodayActivities] = useState<TodayActivity[]>([]);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [stats, setStats] = useState({
    upcomingAppointments: 0,
    unreadMessages: 0,
    activePrograms: 0,
    pendingForms: 0,
    weeklyStreak: 0,
    exercisesCompleted: 0
  });

  // Exercise platform specific state
  const [todayWorkout, setTodayWorkout] = useState<{
    id: string;
    programName: string;
    workoutName?: string;
    exerciseCount: number;
    estimatedMinutes?: number;
    assignmentType: 'workout_plan' | 'rehab_program' | 'exercise_prescription';
    prescriptionId?: string;
    isCompleted?: boolean;
    sessionId?: string;
  } | null>(null);
  const [progressSummary, setProgressSummary] = useState({
    currentStreak: 0,
    totalSessions: 0,
    lastSessionDate: null as string | null
  });

  // Exercise Now modal state
  const [showExerciseNowModal, setShowExerciseNowModal] = useState(false);

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!features) return;
      
      setLoading(true);
      setTodayWorkout(null); // Reset today's workout

      try {
        const promises = [];
        const promiseMap: Record<string, number> = {};
        let promiseIndex = 0;
        
        // Fetch based on enabled features
        if (hasFeature('appointments')) {
          promiseMap['appointments'] = promiseIndex++;
          promises.push(patientApiService.getMyAppointments());
        }
        
        if (hasFeature('messaging')) {
          promiseMap['messages'] = promiseIndex++;
          promises.push(patientApiService.getMyMessages());
        }
        
        if (hasFeature('rehab_programs') || hasFeature('workout_plans')) {
          promiseMap['programs'] = promiseIndex++;
          promises.push(patientApiService.getMyExercisePrograms());
        }

        if (hasFeature('classes')) {
          promiseMap['classes'] = promiseIndex++;
          promises.push(patientApiService.getMyClassEnrollments());
        }

        // Fetch progress summary for exercise platform
        if (isExercisePlatform) {
          promiseMap['progressSummary'] = promiseIndex++;
          promises.push(patientApiService.getProgressSummary());
        }
        
        //if (hasFeature('forms')) {
          //promiseMap['forms'] = promiseIndex++;
          //promises.push(patientApiService.getMyPendingForms());
        //}
        
        const results = await Promise.allSettled(promises);
        
        // Process results
        const activities: TodayActivity[] = [];
        const recent: RecentActivity[] = [];
        const newStats = { ...stats };
        
        // Process appointments
        if (promiseMap['appointments'] !== undefined) {
          const appointmentResult = results[promiseMap['appointments']];
          if (appointmentResult.status === 'fulfilled' && Array.isArray(appointmentResult.value)) {
            const appointments = appointmentResult.value;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            
            // Today's appointments
            appointments
              .filter(apt => {
                const aptDate = new Date(apt.appointment_date+'T00:00:00');
                return isToday(aptDate) && apt.status === 'scheduled';
              })
              .forEach(apt => {
                activities.push({
                  id: apt.id,
                  type: 'appointment',
                  time: format(new Date(apt.appointment_date + ' ' + apt.start_time), 'h:mm a'),
                  title: apt.appointment_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                  subtitle: apt.provider_name || 'Healthcare Provider',
                  status: apt.is_telehealth ? 'Telehealth' : 'In-Person',
                  icon: Clock,
                  color: 'bg-purple-500',
                  action: () => navigate(`/appointments/${apt.id}`)
                });
              });
            
            // Count upcoming
            newStats.upcomingAppointments = appointments.filter(
              apt => new Date(apt.appointment_date) >= today && apt.status === 'scheduled'
            ).length;
            
            // Add to recent activities
            appointments
              .filter(apt => apt.status === 'completed')
              .slice(0, 3)
              .forEach(apt => {
                recent.push({
                  id: apt.id,
                  type: 'appointment',
                  title: 'Completed appointment',
                  description: apt.appointment_type.replace(/_/g, ' '),
                  timestamp: new Date(apt.appointment_date).getTime(),
                  icon: CheckCircle
                });
              });
          }
        }
        
        // Process exercise programs
        if (promiseMap['programs'] !== undefined) {
          const programResult = results[promiseMap['programs']];
          if (programResult.status === 'fulfilled' && Array.isArray(programResult.value)) {
            const programs = programResult.value.filter(p => p.assignment_type != "exercise_prescription" && p.assignment_type != "exercise_now");
            
            // Get today's ISO day
            const todayJSDay = new Date().getDay(); // 0=Sun, 1=Mon...6=Sat
            const todayISODay = jsToISODay(todayJSDay); // 1=Mon...7=Sun
            
            // Process each active program to check for today's exercises
            for (const program of programs.filter(p => p.status === 'active')) {
              try {
                let hasExerciseToday = false;
                let programName = 'Exercise Program';
                let workoutName: string | undefined;
                let exerciseCount = 0;
                let estimatedMinutes: number | undefined;
                let prescriptionId: string | undefined;
                
                if (program.assignment_type === 'workout_plan' && program.workout_plan_id) {
                  // Fetch workout plan details
                  const [workoutPlan, progress] = await Promise.all([
                    patientApiService.getWorkoutPlan(program.workout_plan_id).catch(() => null),
                    patientApiService.getAssignmentProgress(program.id).catch(() => ({ current_week: 1 }))
                  ]);

                  if (workoutPlan) {
                    programName = workoutPlan.name || 'Workout Plan';
                  }

                  // Get current week's schedule
                  const currentWeek = progress?.current_week || 1;
                  const weekSchedule = await patientApiService.getWorkoutWeekSchedule(
                    program.workout_plan_id,
                    currentWeek
                  ).catch(() => []);

                  // Check if today has a workout scheduled (compare ISO days)
                  const todayWorkout = weekSchedule.find((s: any) => s.day_of_week === todayISODay);

                  if (todayWorkout && todayWorkout.exercise_prescription) {
                    hasExerciseToday = true;

                    // Extract workout name, duration, and prescription ID
                    workoutName = todayWorkout.exercise_prescription.name;
                    estimatedMinutes = todayWorkout.exercise_prescription.estimated_duration_minutes;
                    prescriptionId = todayWorkout.exercise_prescription_id || todayWorkout.exercise_prescription?.id;

                    // Count exercises in modules
                    if (todayWorkout.exercise_prescription.modules) {
                      exerciseCount = todayWorkout.exercise_prescription.modules.reduce(
                        (sum: number, module: any) => sum + (module.exercises?.length || 0),
                        0
                      );
                    }
                  }
                  
                } else if (program.assignment_type === 'rehab_program' && program.rehab_program_id) {
                  // Fetch rehab program details
                  const [rehabProgram, progress] = await Promise.all([
                    patientApiService.getRehabProgram(program.rehab_program_id).catch(() => null),
                    patientApiService.getAssignmentProgress(program.id).catch(() => ({ current_phase: 1 }))
                  ]);

                  if (rehabProgram) {
                    programName = rehabProgram.name || 'Rehab Program';
                  }

                  // Get current phase schedule
                  const currentPhase = progress?.current_phase || 1;
                  const phaseSchedule = await patientApiService.getRehabPhaseSchedule(
                    program.rehab_program_id,
                    currentPhase
                  ).catch(() => []);

                  if (phaseSchedule && phaseSchedule.length > 0) {
                    const frequency = phaseSchedule[0].frequency_per_week || 3;

                    // Map frequency to scheduled days (ISO format)
                    const dayMapping: { [key: number]: number[] } = {
                      1: [3], // Wednesday
                      2: [2, 4], // Tuesday, Thursday
                      3: [1, 3, 5], // Monday, Wednesday, Friday
                      4: [1, 2, 4, 5], // Mon, Tue, Thu, Fri
                      5: [1, 2, 3, 4, 5], // Weekdays
                      6: [1, 2, 3, 4, 5, 6], // Mon-Sat
                      7: [1, 2, 3, 4, 5, 6, 7] // Daily
                    };

                    const scheduledDays = dayMapping[frequency] || [1, 3, 5];
                    hasExerciseToday = scheduledDays.includes(todayISODay);

                    // Count exercises and get duration from prescription
                    if (hasExerciseToday && phaseSchedule[0].exercise_prescription) {
                      const prescription = phaseSchedule[0].exercise_prescription;
                      estimatedMinutes = prescription.estimated_duration_minutes;
                      prescriptionId = phaseSchedule[0].exercise_prescription_id || prescription.id;

                      if (prescription.exercises) {
                        exerciseCount = prescription.exercises.length;
                      }
                    }
                  }
                  
                } else if (program.assignment_type === 'exercise_prescription' && program.exercise_prescription_id) {
                  // Direct prescription
                  const prescription = await patientApiService.getExerciseProgram(
                    program.exercise_prescription_id
                  ).catch(() => null);

                  if (prescription) {
                    programName = prescription.name || 'Exercise Program';
                    estimatedMinutes = prescription.estimated_duration_minutes;
                    prescriptionId = program.exercise_prescription_id;

                    // Check schedule
                    if (prescription.schedule?.rehab_frequency) {
                      const frequency = prescription.schedule.rehab_frequency.times_per_week || 3;

                      // Map frequency to scheduled days
                      const dayMapping: { [key: number]: number[] } = {
                        1: [3],
                        2: [2, 4],
                        3: [1, 3, 5],
                        4: [1, 2, 4, 5],
                        5: [1, 2, 3, 4, 5],
                        6: [1, 2, 3, 4, 5, 6],
                        7: [1, 2, 3, 4, 5, 6, 7]
                      };

                      const scheduledDays = dayMapping[frequency] || [1, 3, 5];
                      hasExerciseToday = scheduledDays.includes(todayISODay);

                      // Count exercises
                      if (hasExerciseToday) {
                        if (prescription.exercises) {
                          exerciseCount = prescription.exercises.length;
                        } else if (prescription.modules) {
                          exerciseCount = prescription.modules.reduce(
                            (sum: number, module: any) => sum + (module.exercises?.length || 0),
                            0
                          );
                        }
                      }
                    }
                  }
                }
                
                // Add to today's activities if scheduled for today
                if (hasExerciseToday) {
                  activities.push({
                    id: program.id,
                    type: 'exercise',
                    title: workoutName || programName,
                    subtitle: `${exerciseCount} exercise${exerciseCount !== 1 ? 's' : ''}`,
                    icon: Dumbbell,
                    color: 'bg-green-500',
                    action: () => navigate(`/programs/${program.id}/session/${format(new Date(), 'yyyy-MM-dd')}`),
                    // Store extra data for exercise platform
                    _workoutData: {
                      id: program.id,
                      programName: programName,
                      workoutName: workoutName,
                      exerciseCount: exerciseCount,
                      estimatedMinutes: estimatedMinutes,
                      assignmentType: program.assignment_type,
                      prescriptionId: prescriptionId
                    }
                  });
                }
                
              } catch (error) {
                console.error(`Error processing program ${program.id}:`, error);
              }
            }
            
            newStats.activePrograms = programs.filter(p => p.status === 'active').length;
          }
        }
        
        // Process messages
        if (promiseMap['messages'] !== undefined) {
          const messageResult = results[promiseMap['messages']];
          if (messageResult.status === 'fulfilled' && Array.isArray(messageResult.value)) {
            const threads = messageResult.value;
            newStats.unreadMessages = threads.reduce((sum, thread) => sum + (thread.unread_count || 0), 0);
            
            // Add recent messages to activity
            threads
              .filter(thread => thread.last_message_at)
              .sort((a, b) => b.last_message_at - a.last_message_at)
              .slice(0, 3)
              .forEach(thread => {
                recent.push({
                  id: thread.id,
                  type: 'message',
                  title: `New message from ${thread.last_sender_name || 'Provider'}`,
                  description: thread.last_message_preview || '',
                  timestamp: thread.last_message_at,
                  icon: MessageSquare
                });
              });
          }
        }
        
        // Process forms
        if (promiseMap['forms'] !== undefined) {
          const formResult = results[promiseMap['forms']];
          if (formResult.status === 'fulfilled' && Array.isArray(formResult.value)) {
            newStats.pendingForms = formResult.value.length;
          }
        }

        // Process progress summary for exercise platform
        if (promiseMap['progressSummary'] !== undefined) {
          const summaryResult = results[promiseMap['progressSummary']];
          if (summaryResult.status === 'fulfilled' && summaryResult.value) {
            const summary = summaryResult.value;
            setProgressSummary({
              currentStreak: summary.current_streak || 0,
              totalSessions: summary.total_sessions || 0,
              lastSessionDate: summary.last_session_date || null
            });
          }
        }
        
        // Process class enrollments (today only)
        if (promiseMap['classes'] !== undefined) {
          const classResult = results[promiseMap['classes']];
          if (classResult.status === 'fulfilled' && Array.isArray(classResult.value)) {
            classResult.value
              .filter((e: any) => {
                const session = e.class_session;
                if (!session?.session_date) return false;
                const sessionDate = new Date(session.session_date + 'T00:00:00');
                return isToday(sessionDate)
                  && ['enrolled', 'checked_in', 'waitlisted'].includes(e.status);
              })
              .forEach((enrollment: any) => {
                const session = enrollment.class_session;
                const definition = enrollment.class_definition;
                activities.push({
                  id: `class-${enrollment.id}`,
                  type: 'class',
                  time: session?.start_time
                    ? format(new Date(session.session_date + ' ' + session.start_time), 'h:mm a')
                    : undefined,
                  title: definition?.name || 'Class',
                  subtitle: definition?.description || undefined,
                  status: enrollment.status === 'waitlisted' ? 'Waitlisted' : undefined,
                  icon: Users,
                  color: 'bg-indigo-500',
                  action: () => navigate('/my-classes'),
                  _classData: {
                    enrollment_id: enrollment.id,
                    class_session_id: session?.id,
                    class_definition_id: definition?.id,
                  },
                });
              });
          }
        }

        // Sort activities by time
        activities.sort((a, b) => {
          if (!a.time || !b.time) return 0;
          return a.time.localeCompare(b.time);
        });
        
        // Sort recent activities by timestamp
        recent.sort((a, b) => b.timestamp - a.timestamp);
        
        setTodayActivities(activities);
        setRecentActivities(recent.slice(0, 5)); // Keep only 5 most recent
        setStats(newStats);

        // Set today's workout for exercise platform (first exercise activity)
        if (isExercisePlatform) {
          const firstExercise = activities.find(a => a.type === 'exercise' && a._workoutData);
          if (firstExercise && firstExercise._workoutData) {
            const workoutData = firstExercise._workoutData;

            // Check if this specific workout has been completed today
            let isCompleted = false;
            let sessionId: string | undefined;

            try {
              const todayStr = format(new Date(), 'yyyy-MM-dd');
              const todayCompletions = await patientApiService.getProgressSessions({
                start_date: todayStr,
                end_date: todayStr,
                per_page: 50
              } as any);

              const completions = todayCompletions?.sessions || [];

              // Match by prescription_id for precise matching (handles two-a-days)
              if (workoutData.prescriptionId) {
                const matchingCompletion = completions.find(
                  (c: any) => c.prescription_id === workoutData.prescriptionId &&
                              (c.status === 'completed' || c.status === 'partial')
                );
                if (matchingCompletion) {
                  isCompleted = true;
                  sessionId = matchingCompletion.id;
                }
              }

              // Fallback: match by assignment_id if no prescription match
              if (!isCompleted) {
                const fallbackCompletion = completions.find(
                  (c: any) => c.assignment_id === workoutData.id &&
                              (c.status === 'completed' || c.status === 'partial')
                );
                if (fallbackCompletion) {
                  isCompleted = true;
                  sessionId = fallbackCompletion.id;
                }
              }
            } catch (error) {
              console.warn('Could not check completion status:', error);
            }

            setTodayWorkout({
              id: workoutData.id,
              programName: workoutData.programName,
              workoutName: workoutData.workoutName,
              exerciseCount: workoutData.exerciseCount,
              estimatedMinutes: workoutData.estimatedMinutes,
              assignmentType: workoutData.assignmentType,
              prescriptionId: workoutData.prescriptionId,
              isCompleted,
              sessionId
            });
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [features, hasFeature, isExercisePlatform]);

  // Build quick action cards based on features - REMOVED MESSAGES FROM HERE
  const quickActions = [];
  
  if (hasFeature('clinical_records')) {
    quickActions.push({
      title: 'Health Records',
      path: '/records',
      icon: FileText,
      description: 'View your medical history',
      color: 'bg-blue-500'
    });
  }
  
  if (hasFeature('prescriptions')) {
    quickActions.push({
      title: 'Prescriptions',
      path: '/prescriptions',
      icon: Heart,
      description: 'Manage medications',
      color: 'bg-red-500'
    });
  }
  
  if (hasFeature('rehab_programs') || hasFeature('workout_plans')) {
    quickActions.push({
      title: 'Exercise Programs',
      path: '/programs',
      icon: Dumbbell,
      description: `${stats.activePrograms} active ${stats.activePrograms === 1 ? 'program' : 'programs'}`,
      color: 'bg-green-500',
      badge: stats.activePrograms > 0 ? stats.activePrograms : undefined // Only show badge if > 0
    });
  }
  
  if (hasFeature('appointments')) {
    quickActions.push({
      title: 'Appointments',
      path: '/appointments',
      icon: Clock,
      description: `${stats.upcomingAppointments} upcoming`,
      color: 'bg-purple-500',
      badge: stats.upcomingAppointments > 0 ? stats.upcomingAppointments : undefined // Only show badge if > 0
    });
  }
  
  // Note: Browse Workouts and Progress tiles removed for exercise platform
  // - Browse Workouts: not yet functional
  // - Progress: accessible via navigation tab

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-16 lg:pb-0">
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-6">
              <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-4 w-64 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </header>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="h-6 w-32 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="space-y-3">
              <div className="h-16 bg-gray-100 rounded animate-pulse" />
              <div className="h-16 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-16 lg:pb-0">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {patient?.first_name || 'there'}!
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              {isStandalone 
                ? 'Your fitness journey starts here'
                : 'Manage your health information and activities'
              }
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isExercisePlatform ? (
          /* Exercise Platform Layout */
          <div className="space-y-6">
            {/* Today's Workout Card */}
            <TodaysWorkoutCard workout={todayWorkout} loading={loading} hasActivePlan={stats.activePrograms > 0} />

            {/* Exercise Now - only for exercise platform (not rehab-only) */}
            {hasFeature('workout_plans') && (
              <ExerciseNowCard onPress={() => setShowExerciseNowModal(true)} />
            )}

            {/* Motivation Card */}
            <MotivationCard
              currentStreak={progressSummary.currentStreak}
              totalSessions={progressSummary.totalSessions}
              lastSessionDate={progressSummary.lastSessionDate}
              loading={loading}
            />
          </div>
        ) : (
          /* Standard EHR Layout */
          <>
            {/* Today's Schedule Widget */}
            {(hasFeature('appointments') || hasFeature('rehab_programs') || hasFeature('workout_plans') || hasFeature('classes')) && (
              <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Today's Schedule</h2>
                  <button
                    onClick={() => navigate('/calendar')}
                    className="text-sm text-primary hover:text-primary/80 flex items-center"
                  >
                    View Calendar
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                </div>

                {todayActivities.length > 0 ? (
                  <div className="space-y-3">
                    {todayActivities.map((activity) => {
                      const Icon = activity.icon;
                      return (
                        <button
                          key={activity.id}
                          onClick={activity.action}
                          className="w-full flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                        >
                          <div className={`p-2 rounded-lg ${activity.color} text-white mr-4`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center">
                              <p className="font-medium text-gray-900">{activity.title}</p>
                              {activity.time && (
                                <span className="ml-2 text-sm text-gray-500">{activity.time}</span>
                              )}
                            </div>
                            {activity.subtitle && (
                              <p className="text-sm text-gray-500 mt-0.5">{activity.subtitle}</p>
                            )}
                          </div>
                          {activity.status && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                              {activity.status}
                            </span>
                          )}
                          <ChevronRight className="h-5 w-5 text-gray-400 ml-2" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No activities scheduled for today</p>
                    {(hasFeature('appointments') || hasFeature('rehab_programs')) && (
                      <button
                        onClick={() => navigate(hasFeature('appointments') ? '/appointments' : '/programs')}
                        className="mt-3 text-sm text-primary hover:text-primary/80"
                      >
                        Schedule something
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.path}
                    onClick={() => navigate(action.path)}
                    className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow text-left relative"
                  >
                    {action.badge && (
                      <span className="absolute top-4 right-4 h-6 min-w-[24px] px-1.5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {action.badge > 99 ? '99+' : action.badge}
                      </span>
                    )}
                    <div className={`inline-flex p-3 rounded-lg ${action.color} text-white mb-4`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900">{action.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{action.description}</p>
                  </button>
                );
              })}
            </div>

            {/* Recent Activity */}
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>

              {recentActivities.length > 0 ? (
                <div className="space-y-4">
                  {recentActivities.map((activity) => {
                    const Icon = activity.icon;
                    return (
                      <div key={activity.id} className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                          <p className="text-sm text-gray-500">{activity.description}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500">No recent activity to display.</p>
              )}
            </div>
          </>
        )}
      </main>

      {/* Exercise Now Modal */}
      <ExerciseNowModal
        isOpen={showExerciseNowModal}
        onClose={() => setShowExerciseNowModal(false)}
      />
    </div>
  );
}